/**
 * Who may edit, list, or delete a piece on this browser.
 * A local draft (never saved, no owner) stays usable signed out, like "New".
 * Anything tied to an account (saved to the server, or stamped with an owner)
 * needs that same account signed in.
 */
export interface ActivePieceAccess {
  /** Signed-in user id, or null when signed out. */
  userId: string | null;
  /** An own piece (draft or saved) is open, not a public template. */
  hasOwnPiece: boolean;
  /** A public template is open instead. */
  templateOpen: boolean;
  published: boolean;
  onServer: boolean;
  ownerId: string | null;
}

export function belongsToSignedInUser(
  piece: { onServer: boolean; ownerId: string | null },
  userId: string | null,
): boolean {
  if (!piece.onServer && piece.ownerId == null) return true;
  return userId != null && piece.ownerId === userId;
}

/** Edit (and Publish) for the open own piece. Published pieces are locked. */
export function ownPieceEditable(state: ActivePieceAccess): boolean {
  if (state.templateOpen || !state.hasOwnPiece || state.published) return false;
  return belongsToSignedInUser(state, state.userId);
}

/** Delete for the open own piece, private or published. */
export function ownPieceDeletable(state: ActivePieceAccess): boolean {
  if (state.templateOpen || !state.hasOwnPiece) return false;
  return belongsToSignedInUser(state, state.userId);
}

/** My Pieces row from the local store. Account copies stay hidden for anyone else. */
export function localRowVisible(row: { savedToServer: boolean; ownerId?: string | null }, userId: string | null): boolean {
  return belongsToSignedInUser({ onServer: row.savedToServer, ownerId: row.ownerId ?? null }, userId);
}

/** Delete on a public template: the server's own/can_delete flags, only while signed in. */
export function templateDeletable(
  template: { mine?: boolean; can_delete?: boolean } | null,
  userId: string | null,
): boolean {
  if (!template || userId == null) return false;
  return template.mine === true || template.can_delete === true;
}
